import { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/base/SEOHead';
import { categories, featuredArticles, faqItems } from '@/mocks/siteData';

function HeroSection() {
  return (
    <section className="relative w-full h-[520px] md:h-[620px] overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20Japanese%20animation%20film%20reels%20and%20storyboard%20frames%20floating%20in%20warm%20atmospheric%20space%2C%20cinematic%20composition%20with%20golden%20and%20deep%20red%20tones%2C%20ethereal%20light%20rays%2C%20artistic%20cultural%20heritage%20aesthetic%2C%20no%20explicit%20content%2C%20elegant%20museum-quality%20visual&width=1600&height=900&seq=hero-bg-anime-01&orientation=landscape"
        alt="日本里番动画文化研究"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/65"></div>
      <div className="absolute inset-0 flex items-center justify-center px-4 md:px-6">
        <div className="text-center max-w-3xl w-full">
          <p className="text-background-50/80 text-sm md:text-base font-medium tracking-widest mb-4 animate-fadeIn">
            里 番 文 化 研 究 所
          </p>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-6 leading-tight">
            日本里番完整指南
          </h1>
          <p className="text-background-100/80 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
            全面解析日本里番的定义、历史起源、主要类型分类及相关文化背景，
            帮助成年用户深入了解这一亚文化现象的内容特征与合规观看须知。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/categories"
              className="px-8 py-3.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap inline-flex items-center gap-2"
            >
              <i className="ri-stack-line"></i>
              探索分类
            </Link>
            <Link
              to="/about"
              className="px-8 py-3.5 bg-background-50/15 backdrop-blur-sm text-background-50 border border-background-50/30 rounded-full font-medium text-sm hover:bg-background-50/25 transition-all duration-300 whitespace-nowrap inline-flex items-center gap-2"
            >
              <i className="ri-information-line"></i>
              了解我们
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutIntro() {
  return (
    <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-medium text-primary-600 bg-primary-100 px-3 py-1.5 rounded-full">
              关于我们
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-4 mb-5">
              理性认识里番文化，<br />建立客观认知框架
            </h2>
            <p className="text-foreground-600 leading-relaxed mb-5">
              里番文化研究所成立于2018年，是中文互联网领域中首个专注于日本里番动画文化学术研究的独立平台。
              我们秉持客观、理性、专业的立场，致力于为成年用户提供关于日本里番动画的系统性知识——
              包括其历史发展脉络、类型分类体系、产业运作模式以及合规观看指南。
            </p>
            <p className="text-foreground-600 leading-relaxed mb-6">
              我们的研究团队由动画文化学者、媒体研究者及资深动画评论人组成，已累计发表深度研究文章超过500篇，建立了中文领域最完整的日本里番动画数据库。
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors group"
            >
              了解更多
              <i className="ri-arrow-right-line transition-transform duration-200 group-hover:translate-x-1"></i>
            </Link>
          </div>
          <div className="relative">
            <img
              src="https://readdy.ai/api/search-image?query=Academic%20research%20library%20with%20Japanese%20animation%20art%20books%20displayed%20on%20wooden%20table%2C%20warm%20reading%20room%20with%20soft%20natural%20light%20through%20large%20windows%2C%20scholarly%20and%20elegant%20atmosphere%2C%20clean%20modern%20aesthetic%2C%20no%20people&width=800&height=600&seq=about-intro-01&orientation=landscape"
              alt="里番文化研究所学术研究"
              className="w-full rounded-lg object-cover aspect-[4/3]"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturedArticles() {
  const articleImages: Record<string, string> = {
    'article-1': 'https://readdy.ai/api/search-image?query=Japanese%20animation%20studio%20workspace%20with%20drawing%20table%20and%20storyboard%20frames%20on%20wall%2C%20warm%20ambient%20lighting%2C%20professional%20anime%20production%20environment%2C%20clean%20and%20organized%2C%20editorial%20photography%20with%20soft%20shadows&width=600&height=375&seq=article-def-01&orientation=landscape',
    'article-2': 'https://readdy.ai/api/search-image?query=Retro%20Japanese%20VHS%20tape%20collection%20displayed%20on%20wooden%20shelves%20with%20vintage%20anime%20artwork%20covers%2C%201980s%20aesthetic%2C%20warm%20nostalgic%20lighting%2C%20cultural%20archival%20photography&width=600&height=375&seq=article-hist-01&orientation=landscape',
    'article-3': 'https://readdy.ai/api/search-image?query=Japanese%20Akihabara%20district%20with%20anime%20posters%20and%20electronics%20stores%2C%20urban%20cultural%20landscape%2C%20warm%20evening%20neon%20glow%2C%20documentary%20style%20photography%20with%20cinematic%20composition&width=600&height=375&seq=article-cult-01&orientation=landscape',
    'article-5': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20home%20viewing%20setup%20with%20large%20monitor%20and%20comfortable%20lighting%2C%20clean%20aesthetic%20room%2C%20soft%20warm%20ambient%20light%2C%20editorial%20interior%20photography&width=600&height=375&seq=article-guide-01&orientation=landscape',
  };

  return (
    <section className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="text-xs font-medium text-accent-700 bg-accent-100 px-3 py-1.5 rounded-full">
              精选文章
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-4">
              深度研究 · 精选推荐
            </h2>
          </div>
          <Link
            to="/categories"
            className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors group"
          >
            查看全部
            <i className="ri-arrow-right-line transition-transform duration-200 group-hover:translate-x-1"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featuredArticles.map((article) => (
            <Link
              key={article.id}
              to={`/articles/${article.slug}`}
              className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200/70 transition-all duration-300"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={articleImages[article.id]}
                  alt={article.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-4 md:p-5">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs text-primary-600 bg-primary-100 px-2.5 py-1 rounded-full">
                    {article.categoryName}
                  </span>
                  <span className="text-xs text-foreground-400">{article.readTime} 分钟阅读</span>
                </div>
                <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-2 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-xs md:text-sm text-foreground-500 leading-relaxed line-clamp-2">
                  {article.excerpt}
                </p>
              </div>
            </Link>
          ))}
        </div>

        <div className="sm:hidden mt-6 text-center">
          <Link
            to="/categories"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary-600"
          >
            查看全部文章
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}

function CategoryOverview() {
  const colorMap: Record<string, { bg: string; text: string; border: string }> = {
    primary: { bg: 'bg-primary-100', text: 'text-primary-700', border: 'hover:border-primary-200/70' },
    accent: { bg: 'bg-accent-100', text: 'text-accent-700', border: 'hover:border-accent-200/70' },
    secondary: { bg: 'bg-secondary-100', text: 'text-secondary-700', border: 'hover:border-secondary-200/70' },
  };

  return (
    <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-xs font-medium text-secondary-700 bg-secondary-100 px-3 py-1.5 rounded-full">
            分类导航
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-4 mb-4">
            五大研究领域
          </h2>
          <p className="text-foreground-500 max-w-xl mx-auto text-sm">
            我们围绕日本里番动画文化建立系统性的知识框架，从历史、类型、文化、产业、合规五个维度展开深度研究。
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {categories.map((cat) => {
            const colors = colorMap[cat.color] || colorMap.primary;
            return (
              <Link
                key={cat.id}
                to={`/categories/${cat.slug}`}
                className={`group bg-background-50 border border-background-200/70 rounded-lg p-5 transition-all duration-300 ${colors.border} hover:-translate-y-1`}
              >
                <div className={`w-11 h-11 rounded-lg ${colors.bg} flex items-center justify-center mb-4`}>
                  <i className={`${cat.icon} text-xl ${colors.text}`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                  {cat.name}
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed mb-3 line-clamp-3">
                  {cat.description}
                </p>
                <span className="text-xs text-foreground-400">
                  {cat.articleCount} 篇文章
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-xs font-medium text-primary-600 bg-primary-100 px-3 py-1.5 rounded-full">
            常见问题
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-4 mb-4">
            关于里番，你最想了解的问题
          </h2>
        </div>

        <div className="space-y-3">
          {faqItems.map((item, i) => (
            <div
              key={i}
              className="bg-background-100 border border-background-200/70 rounded-lg overflow-hidden"
            >
              <button
                className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <span className="font-medium text-sm text-foreground-800 pr-4">{item.question}</span>
                <i
                  className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-300 text-lg flex-shrink-0 ${
                    openIndex === i ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              <div
                className={`px-5 overflow-hidden transition-all duration-300 ${
                  openIndex === i ? 'pb-4 max-h-96' : 'max-h-0'
                }`}
              >
                <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      return;
    }
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9srl758o1vn02ekul80', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: formData.get('email') as string }).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok && (parsed as { code?: string }).code === 'OK') {
        setStatus('success');
        setEmail('');
      } else {
        const msg = (parsed as { meta?: { message?: string } })?.meta?.message
          || (parsed as { message?: string })?.message
          || text
          || '订阅失败，请稍后重试';
        setStatus('error');
        setErrorMsg(String(msg));
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络异常，请检查连接后重试');
    }
  };

  return (
    <section className="py-16 md:py-20 px-4 md:px-6 bg-background-900">
      <div className="max-w-xl mx-auto text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
          订阅我们的研究简报
        </h2>
        <p className="text-background-400 text-sm mb-8">
          定期接收关于日本里番动画文化的最新研究文章、产业动态与深度分析。仅面向成年用户。
        </p>

        {status === 'success' ? (
          <div className="bg-background-800/50 border border-background-700/50 rounded-lg px-6 py-8">
            <div className="w-12 h-12 rounded-full bg-primary-500/20 flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-line text-2xl text-primary-400"></i>
            </div>
            <p className="text-background-200 font-medium">订阅成功！</p>
            <p className="text-background-500 text-sm mt-1">感谢您的关注，我们会定期发送研究简报至您的邮箱。</p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="flex flex-col sm:flex-row gap-3"
          >
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="输入您的电子邮箱"
              required
              className="flex-1 px-5 py-3.5 bg-background-800 border border-background-700 rounded-full text-sm text-background-100 placeholder:text-background-500 focus:outline-none focus:border-primary-500 transition-colors"
            />
            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="px-7 py-3.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all duration-300 disabled:opacity-60 whitespace-nowrap"
            >
              {status === 'loading' ? '订阅中...' : '立即订阅'}
            </button>
          </form>
        )}
        {status === 'error' && (
          <p className="text-red-400 text-xs mt-3">{errorMsg}</p>
        )}
      </div>
    </section>
  );
}

export default function HomePage() {
  const homeSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '里番文化研究所',
        url: 'https://successlsw.cn',
        description: '致力于日本里番动画文化的学术研究与知识传播',
        foundingDate: '2018',
      },
      {
        '@type': 'WebSite',
        name: '里番文化研究所',
        url: 'https://successlsw.cn',
        description: '全面解析日本里番的定义、历史起源、主要类型分类及相关文化背景',
        inLanguage: 'zh-CN',
      },
      {
        '@type': 'WebPage',
        name: '首页',
        url: 'https://successlsw.cn',
        isPartOf: { '@id': 'https://successlsw.cn/#website' },
        about: { '@id': 'https://successlsw.cn/#organization' },
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: 'https://readdy.ai/api/search-image?query=Abstract%20artistic%20Japanese%20animation%20film%20reels%20and%20storyboard%20frames%20floating%20in%20warm%20atmospheric%20space&width=1600&height=900&seq=hero-bg-anime-01&orientation=landscape',
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqItems.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
    ],
  };

  return (
    <Layout>
      <SEOHead
        title="日本里番完整指南 · 类型解析与观看须知"
        description="全面解析日本里番的定义、历史起源、主要类型分类及相关文化背景，帮助成年用户深入了解日本里番的内容特征、平台规范与合规观看须知，理性认识这一亚文化现象。"
        keywords="日本里番,日本里番类型,日本里番历史,里番动画文化,成人动画分类,日本动画亚文化,里番观看须知"
        canonicalUrl="https://successlsw.cn/"
        ogImage="https://readdy.ai/api/search-image?query=Abstract%20artistic%20Japanese%20animation%20film%20reels%20and%20storyboard%20frames%20floating%20in%20warm%20atmospheric%20space&width=1600&height=900&seq=hero-bg-anime-01&orientation=landscape"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homeSchema) }}
      />

      <HeroSection />
      <AboutIntro />
      <FeaturedArticles />
      <CategoryOverview />
      <FAQSection />
      <NewsletterSection />
    </Layout>
  );
}